import { FormEvent, useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ChevronLeft } from "lucide-react"
import {
  deleteAccount,
  getProfile,
  requestDataExport,
  type ProfileResponse,
} from "~/api/auth"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"

type DeleteStep = "idle" | "warn" | "confirm"

export function PrivacyPage() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [exportError, setExportError] = useState<string | null>(null)
  const [exportBusy, setExportBusy] = useState(false)
  const [deleteStep, setDeleteStep] = useState<DeleteStep>("idle")
  const [deletePassword, setDeletePassword] = useState("")
  const [deleteConfirm, setDeleteConfirm] = useState("")
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  useEffect(() => {
    getProfile()
      .then(setProfile)
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  const pendingExport = profile?.user.pending_data_export
  const exportPending = useMemo(
    () => pendingExport?.status === "pending" || pendingExport?.status === "processing",
    [pendingExport],
  )

  async function handleRequestExport() {
    setExportError(null)
    setExportBusy(true)
    try {
      const updated = await requestDataExport()
      setProfile(updated)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Could not request export")
    } finally {
      setExportBusy(false)
    }
  }

  async function handleDeleteAccount(event: FormEvent) {
    event.preventDefault()
    setDeleteError(null)
    setDeleteBusy(true)
    try {
      await deleteAccount(deletePassword, deleteConfirm)
      navigate("/login", { replace: true })
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Could not delete account")
      setDeleteBusy(false)
    }
  }

  if (loading) {
    return (
      <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
        <LoggedInScreenCard>
          <p className="text-[var(--color-text-secondary)]">Loading...</p>
        </LoggedInScreenCard>
      </PageShell>
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
      <header className="mb-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/profile")}
            aria-label="Back to Profile"
            className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronLeft size={20} aria-hidden />
            <span className="sr-only">Back to Profile</span>
          </button>
          <h1 className="flex-1 text-lg font-semibold text-[var(--color-text-primary)]">Privacy</h1>
      </header>

      <div className="space-y-4">
        <Link
          to="/privacy-policy"
          className="block rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4 hover:bg-[var(--color-surface-hover)]"
          aria-label="Open Privacy Policy"
        >
          <h2 className="mb-2 font-medium text-[var(--color-text-primary)]">Privacy Policy</h2>
          <p className="text-sm text-[var(--color-text-secondary)]">
            View our privacy policy and data practices.
          </p>
        </Link>

        <Link
          to="/terms"
          className="block rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4 hover:bg-[var(--color-surface-hover)]"
          aria-label="Open Terms of Service"
        >
          <h2 className="mb-2 font-medium text-[var(--color-text-primary)]">Terms of Service</h2>
          <p className="text-sm text-[var(--color-text-secondary)]">
            View our terms and conditions.
          </p>
        </Link>

        <div className="rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4">
          <h2 className="mb-2 font-medium text-[var(--color-text-primary)]">Download Your Data</h2>
          <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
            Your export includes account details, dogs, stories, Ask Dynamo history, and preferences.
            Passwords, tokens, and billing secrets are excluded. We email a secure copy to your
            verified address, usually within a few minutes (up to 24 hours).
          </p>
          {exportPending ? (
            <p className="text-sm font-medium text-[var(--color-text-primary)]" role="status">
              Export requested — we&apos;ll email you when it&apos;s ready.
            </p>
          ) : (
            <button
              type="button"
              disabled={exportBusy}
              onClick={() => void handleRequestExport()}
              aria-label="Request Data"
              className="rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
            >
              {exportBusy ? "Requesting…" : "Request Data"}
            </button>
          )}
          {exportError && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {exportError}
            </p>
          )}
        </div>

        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <h2 className="mb-2 font-medium text-red-900">Delete Account</h2>
          {deleteStep === "idle" && (
            <>
              <p className="mb-3 text-sm text-red-700">
                Permanently delete your account and all data. This cannot be undone.
              </p>
              <button
                type="button"
                onClick={() => setDeleteStep("warn")}
                aria-label="Delete Account"
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete Account
              </button>
            </>
          )}

          {deleteStep === "warn" && (
            <div className="space-y-3">
              <p className="text-sm text-red-700">
                Deleting removes your dogs, portraits, stories, Ask Dynamo history, and preferences.
                If you have a paid Plus subscription, we attempt to cancel billing; complimentary
                access simply ends. Deletion is immediate once confirmed.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteStep("confirm")}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  Continue
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteStep("idle")}
                  aria-label="Cancel account deletion"
                  className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {deleteStep === "confirm" && (
            <form onSubmit={handleDeleteAccount} className="space-y-3">
              <p className="text-sm text-red-700">
                Enter your password and type <strong>DELETE</strong> to confirm.
              </p>
              <label className="block text-sm text-red-900">
                Current password
                <input
                  type="password"
                  autoComplete="current-password"
                  aria-label="Current password for account deletion"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  required
                  className="mt-1 w-full rounded-lg border border-red-200 bg-white px-3 py-2"
                />
              </label>
              <label className="block text-sm text-red-900">
                Type DELETE
                <input
                  type="text"
                  aria-label="Type DELETE to confirm account deletion"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  required
                  className="mt-1 w-full rounded-lg border border-red-200 bg-white px-3 py-2"
                />
              </label>
              {deleteError && (
                <p className="text-sm text-red-600" role="alert">
                  {deleteError}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={deleteBusy || deleteConfirm !== "DELETE"}
                  aria-label="Confirm delete account"
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteBusy ? "Deleting…" : "Permanently delete"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteStep("idle")
                    setDeletePassword("")
                    setDeleteConfirm("")
                    setDeleteError(null)
                  }}
                  aria-label="Cancel account deletion"
                  className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-800"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
